/**
 * Preferred-locale sync between the server profile and the local i18n layer.
 *
 * Three responsibilities, all wired off the centralized auth state:
 *  1. Register a locale-sync handler so `useLanguage().setLanguage()` can
 *     persist the user's choice to the right preference endpoint (provider vs
 *     user) without the i18n layer knowing anything about identity.
 *  2. For authenticated non-provider users, read their server-saved
 *     `preferredLocale` (providers get theirs from the loaded provider profile).
 *  3. On first availability, prefer the server's `preferredLocale` over the
 *     localStorage value (server is the source of truth for authenticated
 *     users). Applied exactly once so a later manual switch is never clobbered.
 *
 * All sync is best-effort: the underlying API calls swallow errors, so offline
 * / PWA language switching keeps working from localStorage regardless.
 */

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { applyServerLocale } from '@/app/i18n';
import { registerLocaleSyncHandler } from '@/app/i18n/localeSync';
import { provider as providerApi } from '@/shared/api/shop';
import {
  getUserPreferredLocale,
  updateUserPreferredLocale,
} from '@/shared/api/users';
import { authKeys } from './keys';

interface PreferredLocaleSyncArgs {
  isProvider: boolean;
  /** Internal provider id (from the loaded provider profile), if a provider. */
  providerId: string | null;
  /** User identifier (Auth0 sub or internal id) for the non-provider path. */
  userId: string | null;
  /** Server-persisted preferred locale from the loaded provider profile. */
  providerPreferredLocale?: string | null;
}

export const usePreferredLocaleSync = ({
  isProvider,
  providerId,
  userId,
  providerPreferredLocale,
}: PreferredLocaleSyncArgs): void => {
  // (1) Keep the locale-sync bridge pointed at the correct preference endpoint.
  useEffect(() => {
    registerLocaleSyncHandler((locale: string) => {
      if (isProvider && providerId) {
        void providerApi.updatePreferredLocale(providerId, locale);
      } else if (userId) {
        void updateUserPreferredLocale(userId, locale);
      }
      // No identity yet → no-op; the local switch + localStorage already ran.
    });

    return () => registerLocaleSyncHandler(null);
  }, [isProvider, providerId, userId]);

  // (2) Read the non-provider user's server preference. Providers get theirs
  // from the provider profile, so this is gated to the non-provider path. A
  // stray call for a provider (before role resolves) just returns null.
  const { data: userPreferredLocale } = useQuery<string | null>({
    queryKey: authKeys.userPreferredLocale(userId),
    queryFn: () => getUserPreferredLocale(userId as string),
    enabled: !!userId && !isProvider,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // (3) Apply the server preference once PER AUTHENTICATED IDENTITY. AuthProvider
  // mounts once at the app root, so a once-per-hook-lifetime guard would skip the
  // next user's locale after a same-tab logout/login. Keying on identity (and
  // resetting when it clears) makes the next user's server locale win again.
  const serverPreferredLocale = isProvider
    ? providerPreferredLocale
    : userPreferredLocale;
  const identityKey = isProvider
    ? providerId && `provider:${providerId}`
    : userId && `user:${userId}`;

  const appliedIdentityRef = useRef<string | null>(null);
  useEffect(() => {
    // No identity (initial load or post-logout) → reset so the next identity
    // re-applies its own server preference.
    if (!identityKey) {
      appliedIdentityRef.current = null;
      return;
    }
    if (appliedIdentityRef.current === identityKey || !serverPreferredLocale) {
      return;
    }
    appliedIdentityRef.current = identityKey;
    applyServerLocale(serverPreferredLocale);
  }, [identityKey, serverPreferredLocale]);
};
